import clsx from 'clsx';
import { useState } from 'react';
import { useAsyncEffect, useDidUpdate } from 'rooks';
import { useIsCurrentAssetGroup } from '@/assetGroupEditor/assetGroup.store';
import { Checkbox } from '@/core/Checkbox.atom';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { UnitOfMeasure } from '@/core/UnitOfMeasure.atom';
import { sqItemTableEditorStore, sqScalingTableEditorStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { onEnterKeypress } from '@/core/onEnterKeypress.util';
import { onEventExceptDisplayDropdown } from '@/displays/displays.utilities';
import { HierarchyNodeType } from '@/search/search.constants';
import { useSearchActions } from '@/search/search.store';
import { getItemTableTypes, getScalingTableTypes } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { extractDisplayUnitFromIncludedProperties, resolveUnitOfMeasure } from '@/utilities/unit.utilities';
import { isAsset, isDatafile, isDisplay, isHierarchy, isTableDefinition, itemIconClass } from '@/utilities/utilities';
import { SeeqNames } from '@seeqdev/constants';
import { faChevronRight } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import type { SelectedSearchItem } from '@/search/search.types';
import type { AnyProperty } from '@/utilities.types';
import type { HierarchyNodeOutputV1 } from '@seeqdev/api/model/HierarchyNodeOutputV1';

const CONDITION_TYPES = [
  SeeqNames.Types.Condition,
  SeeqNames.Types.StoredCondition,
  SeeqNames.Types.CalculatedCondition,
] as const;

export interface SearchResultProps {
  searchResultIcons: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  item: any;
  items?: any[];
  customIconClasses?: string;
  onClickItem: (item: AnyProperty) => void;
  showItemSelection?: boolean;
  isSelectingAsset?: boolean;
  searchTypes?: string[];
  itemSelected?: boolean;
  isSelected?: boolean;
  isAllUnselectable?: boolean;
  highlightItem?: boolean;
  onSelectItem?: (item: any) => void;
  assetGroupEditorVisible?: boolean;
  showOnlyConditions?: boolean;
  doNotDisableOnEdit?: boolean;
  noNavigate?: boolean;
}

export const SearchResult = ({
  searchResultIcons,
  item,
  items,
  onClickItem,
  customIconClasses,
  onSelectItem,
  isSelectingAsset = false,
  searchTypes,
  itemSelected = false,
  isSelected = false,
  isAllUnselectable = false,
  showItemSelection = false,
  highlightItem = false,
  assetGroupEditorVisible = false,
  showOnlyConditions = false,
  doNotDisableOnEdit = false,
  noNavigate = false,
}: SearchResultProps) => {
  const itemIconClassNames = `${customIconClasses ? customIconClasses : 'sq-fairly-dark-gray'} tw:pr-sq-10 tw:pl-sq-5`;
  const arrowIconClassNames = `${customIconClasses ? customIconClasses : 'text-interactive'} tw:mr-sq-5`;
  const isEditingAssetGroup = useIsCurrentAssetGroup(item.id) && assetGroupEditorVisible;
  const isEditingTableDefinition = useFluxPath(
    sqScalingTableEditorStore,
    () => sqScalingTableEditorStore.tableId === item.id,
  );
  const isEditingItemTable = useFluxPath(sqItemTableEditorStore, () => sqItemTableEditorStore.tableId === item.id);
  const tableTypeProp = item.includedProperties?.[SeeqNames.Properties.TableType]?.value;

  const { clearSelectedItems, toggleSelectedItem } = useSearchActions();
  const isScalingTableThatCanBeNavigated =
    isTableDefinition(item) && getScalingTableTypes().includes(tableTypeProp) && !doNotDisableOnEdit;
  const isItemTableThatCanBeNavigated = isTableDefinition(item) && getItemTableTypes().includes(tableTypeProp);
  const isNavigableHierarchy = isHierarchy(item);
  const isHierarchyNode = item.type === HierarchyNodeType && !!item.node;
  const node = isHierarchyNode ? (item.node as HierarchyNodeOutputV1) : undefined;
  const type = node?.itemMetadata?.type || item.type;
  const isPartOfHierarchyAndHasChildren = isHierarchyNode && !item.node.leaf;
  const showNavigateCaret =
    isAsset(item) ||
    isDatafile(item) ||
    isScalingTableThatCanBeNavigated ||
    isItemTableThatCanBeNavigated ||
    isNavigableHierarchy ||
    isPartOfHierarchyAndHasChildren ||
    item?.hasActiveChildren ||
    (item.ancestors?.length === 1 &&
      item.ancestors?.some((ancestor: any) => ancestor.type === SeeqNames.Types.TableDefinition));
  const resolvedUom = resolveUnitOfMeasure(extractDisplayUnitFromIncludedProperties(item));

  const showIcons =
    (!isSelectingAsset && !searchTypes) ||
    (searchTypes && !searchTypes.includes(SeeqNames.Types.Asset) && searchTypes.includes(type));

  const [isItemSelected, setSelected] = useState(false);
  const [icons, setIcons] = useState<React.ReactNode>(null);

  useDidUpdate(() => setSelected(itemSelected), [itemSelected]);

  useAsyncEffect(async () => {
    const newIcons = await searchResultIcons(item);
    setIcons(newIcons);
  }, [searchResultIcons, item]);

  const unfocusSearchResult = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    (e.target as HTMLElement).blur();
  };

  const isAssetOrAssetNode = isAsset(item) || isAsset(node?.itemMetadata) || type === HierarchyNodeType;

  const renderCheckbox = () => {
    if (showItemSelection && (!isAllUnselectable || assetGroupEditorVisible)) {
      return (
        <Checkbox
          id={`select_${item.id}`}
          label=""
          classes={clsx('tw:mr-sq-5 tw:ml-sq-5', {
            'tw:invisible':
              isTableDefinition(item) ||
              isHierarchy(item) ||
              (!assetGroupEditorVisible && (isAssetOrAssetNode || isDisplay(item) || item?.hasActiveChildren)),
          })}
          isChecked={isItemSelected}
          onClick={() => {
            toggleSelectedItem(item.id, items);
            setSelected((isSelected) => !isSelected);
          }}
          flexAlign="center"
        />
      );
    }

    return <></>;
  };

  const searchResult = (
    <div
      data-testid={icons ? 'searchResultWithIcons' : 'searchResult'}
      aria-label={item.name}
      tabIndex={0}
      onMouseLeave={unfocusSearchResult}
      onClick={() => {
        const isOnlySelectingAssets = searchTypes?.includes(SeeqNames.Types.Asset) && searchTypes.length === 1;
        if (
          (!item.hasActiveChildren && !isOnlySelectingAssets) ||
          (isOnlySelectingAssets && !item.hasActiveChildren && isAsset(item)) ||
          doNotDisableOnEdit
        ) {
          onSelectItem?.(item);
        }
      }}
      className={clsx(
        'flexColumnContainer',
        'itemRow',
        'tw:flex-none',
        'cursorPointer',
        {
          'disabledItemRow disabledLook tw:cursor-not-allowed tw:italic': isEditingAssetGroup && !doNotDisableOnEdit,
          'itemRowSelected regularBorder': isEditingTableDefinition || isEditingItemTable,
          'searchResultWithIcons': !!icons,
        },
        { itemRowSelected: isSelected },
        { highlightFade: highlightItem },
      )}>
      {renderCheckbox()}

      <div
        data-testid="searchResultItem"
        className="flexColumnContainer flexFill searchResultItem tw:justify-between tw:pt-sq-5 tw:pb-sq-5"
        onKeyUp={onEnterKeypress(() => !isEditingAssetGroup && onClickItem(item))}
        onClick={onEventExceptDisplayDropdown(() => {
          if (!isEditingAssetGroup) {
            onClickItem(item);
            clearSelectedItems();
          }
        })}>
        <div className="flexColumnContainer tw:items-center tw:justify-center">
          {isSelectingAsset && icons}
          <Icon
            icon={itemIconClass(item)}
            testId="searchItemIcon"
            type="inherit"
            extraClassNames={itemIconClassNames}
            large={true}
          />
        </div>

        <div className="aggressiveWordBreak flexFill flexRowContainer tw:justify-center">
          <div className="searchResultName">
            <span>{item.name} </span>
            <UnitOfMeasure unitOfMeasure={resolvedUom} />
          </div>
          {item.description && (
            <div className="xsmall tw:pb-sq-5" data-testid="searchResultDescription">
              {item.description}
            </div>
          )}
          {(item.assets?.[0]?.formattedName || item?.datasource?.name) && (
            <div className="xsmall sq-fairly-dark-gray tw:pb-sq-3 tw:italic">
              {item.assets?.[0]?.formattedName || item?.datasource?.name}
            </div>
          )}
        </div>

        <div className="flexRowContainer tw:items-center tw:justify-center">
          <div data-testid="selectOrNavigate" className="flexColumnContainer tw:items-center tw:justify-center">
            {showIcons && icons}

            {showNavigateCaret && !noNavigate && (
              <Icon icon={faChevronRight} type="inherit" extraClassNames={arrowIconClassNames} testId="arrowIcon" />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const getSearchResult = () => {
    return showOnlyConditions && !(CONDITION_TYPES.includes(type) || item.hasActiveChildren) ? undefined : searchResult;
  };

  return (
    <HoverTooltip text={isEditingAssetGroup ? 'SEARCH_DATA.NO_ACTIONS_WHILE_EDITING' : ''}>
      {getSearchResult()}
    </HoverTooltip>
  );
};
